<script lang="ts" module>
	interface PreloadFont {
		type: string;
		href: string;
	}
</script>

<script lang="ts">
	import { createTitle } from "@utils/createTitle.js";
	import type { Snippet } from "svelte";
	import resolveConfig from "tailwindcss/resolveConfig.js";
	import Hubot from "@core/assets/fonts/Hubot/HubotSans.woff2";
	import Inter from "@core/assets/fonts/Inter/Inter.woff2";
	import { tenants } from "@core/config/tenants.js";
	import { Locale } from "@core/schema/Locale.js";
	import { UploadedFileBucket } from "@core/schema/UploadedFileBucket.js";
	import { appName } from "@core/schema/names/appName.js";
	import tailwindConfig from "../../../tailwind.config.js";
	import "../../assets/css/main.css";
	import ogImageSrc from "../../assets/og-image.jpg";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { lockScroll } from "../../state/lockScroll.svelte.js";
	import CallUsBanner from "../CallUsBanner.svelte";
	import Analytics from "./Analytics.svelte";
	import Footer from "./Footer.svelte";
	import HeadFavicons from "./HeadFavicons.svelte";
	import Header from "./Header.svelte";

	const {
		children,
		ogImage,
	}: {
		ogImage?: string;
		children: Snippet;
	} = $props();

	const { ROBOTS, WEB_URL, FILE_BUCKET_URL_MAP } = getGlobalSettings();
	const { pagePath, title, description, keywords, menu, index, tenant } = getGlobalTemplateProps();

	const pageTitle = createTitle(title);
	const ogImageUrl = ogImage ?? ogImageSrc;

	const fontsToPreload: PreloadFont[] = [
		{
			href: Inter,
			type: "font/woff2",
		},
		{
			href: Hubot,
			type: "font/woff2",
		},
	];
	const canonical = tenant.locale in pagePath ? `${WEB_URL}${pagePath[tenant.locale]}` : undefined;
	const themeColor = resolveConfig(tailwindConfig).theme.colors.white;

	$effect(() => {
		document.documentElement.style.overflow = lockScroll.lock ? "hidden" : "auto";
	});
</script>

<svelte:head>
	<link rel="preconnect" href={FILE_BUCKET_URL_MAP[UploadedFileBucket.Storage]} />

	<!-- Preloads of fonts used at the top of pages -->
	{#each fontsToPreload as { href, type } (href)}
		<link rel="preload" as="font" {href} {type} crossorigin="anonymous" />
	{/each}

	<!-- Theme color -->
	<meta name="msapplication-TileColor" content={themeColor} />
	<meta name="theme-color" content={themeColor} />

	<!-- Site name -->
	<meta property="og:site_name" content={appName} />
	<meta name="twitter:site" content={appName} />
	<meta name="application-name" content={appName} />
	<meta name="apple-mobile-web-app-title" content={appName} />

	<!-- Dynamic Open graph tags (Meta) -->
	{#if canonical}
		<meta property="og:url" content={canonical} />
	{/if}
	<meta property="og:image" content={ogImageUrl} />

	<!-- Dynamic Twitter Meta tags -->
	<meta name="twitter:card" content="summary_large_image" />
	<meta property="twitter:domain" content={WEB_URL} />
	{#if canonical}
		<meta property="twitter:url" content={canonical} />
	{/if}
	<meta name="twitter:image" content={ogImageUrl} />

	<!-- Title, description and keywords -->
	<title>{pageTitle}</title>
	<meta property="og:title" content={pageTitle} />
	<meta property="og:title" content={pageTitle} />
	<meta name="twitter:title" content={pageTitle} />
	<meta name="description" content={description} />
	<meta name="og:description" content={description} />
	<meta name="twitter:description" content={description} />
	{#if keywords}
		<meta name="keywords" content={keywords} />
	{/if}

	<!-- Robots -->
	<meta name="robots" content={ROBOTS === "index" && index ? "index, follow" : "noindex, nofollow"} />

	<!-- Canonical and hreflang -->
	{#if canonical}
		<link rel="canonical" href={canonical} />
	{/if}
	{#each Object.values(tenants) as tenant (tenant.locale)}
		{#if tenant.locale in pagePath}
			<link rel="alternate" hreflang={tenant.locale} href="{WEB_URL}{pagePath[tenant.locale]}" />
		{/if}
	{/each}
	<!-- x-default for primary language (cs) -->
	{#if Locale.cs in pagePath}
		<link rel="alternate" hreflang="x-default" href="{WEB_URL}{pagePath[Locale.cs]}" />
	{/if}
</svelte:head>

<HeadFavicons />

<Analytics />

<div class="contents">
	<Header {menu} />

	<main class="grid-container grow relative pt-20 lg:pt-24">
		{@render children()}
	</main>

	<Footer />

	<CallUsBanner />
</div>
